
import { Button } from "@/components/ui/button";
import { Languages } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { LanguageSelector } from "./LanguageSelector";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

interface TranslateButtonProps {
  text?: string;
  size?: "default" | "sm" | "lg" | "icon";
  variant?: "default" | "outline" | "secondary" | "ghost" | "destructive" | "link";
  targetLanguage?: string;
  onTranslated?: (translatedText: string) => void;
  onClick?: () => Promise<void>;  // Add onClick property to support direct click handlers
  loading?: boolean;  // Add loading prop to support external loading state
  tooltipText?: string;  // Add tooltipText prop for accessibility
}

export function TranslateButton({
  text,
  size = "default",
  variant = "outline",
  targetLanguage: initialTargetLanguage,
  onTranslated,
  onClick,
  loading: externalLoading,
  tooltipText,
}: TranslateButtonProps) {
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const [isTranslating, setIsTranslating] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [targetLanguage, setTargetLanguage] = useState(initialTargetLanguage || "en");

  // Determine loading state - either from external prop or internal state
  const isLoading = externalLoading !== undefined ? externalLoading : isTranslating;

  const handleTranslate = async () => {
    // If there's a custom click handler, use that instead
    if (onClick) {
      return onClick();
    }

    if (!text?.trim()) {
      toast({
        title: t("translations.noTextToTranslate"),
        variant: "destructive",
      });
      return;
    }

    try {
      setIsTranslating(true);
      
      console.log("Translating text to", targetLanguage, "Text length:", text.length);

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/translate-text`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
          body: JSON.stringify({
            text,
            targetLang: targetLanguage,
            sourceLang: "auto",
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Translation error:", errorData);
        throw new Error(errorData.error || "Translation failed");
      }

      const result = await response.json();
      console.log("Translation result:", result);
      
      if (onTranslated) {
        onTranslated(result.translatedText);
      } else {
        toast({
          title: t("translations.translationComplete"),
          description: result.translatedText,
        });
      }
      
      setIsDialogOpen(false);
    } catch (error) {
      console.error("Translation error:", error);
      toast({
        title: t("translations.translationFailed"),
        description: error instanceof Error ? error.message : String(error),
        variant: "destructive",
      });
    } finally {
      setIsTranslating(false);
    }
  };

  const handleClick = () => {
    // If a custom click handler is provided and no dialog is needed, just call it directly
    if (onClick && !text) {
      onClick();
      return;
    }
    
    if (initialTargetLanguage) {
      // If target language is provided externally, translate directly
      handleTranslate();
    } else {
      // Otherwise show dialog to select target language
      setIsDialogOpen(true);
    }
  };

  return (
    <>
      <Button
        size={size}
        variant={variant}
        onClick={handleClick}
        disabled={isLoading}
        className="flex items-center gap-1"
        title={tooltipText}
      >
        <Languages className="h-4 w-4" />
        {size !== "icon" && t("common.translate")}
      </Button>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("translations.translateText")}</DialogTitle>
            <DialogDescription>
              {t("translations.selectTargetLanguage")}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="flex flex-col gap-2">
              <h3 className="font-medium text-sm">{t("translations.targetLanguage")}</h3>
              <LanguageSelector 
                selectedLanguage={targetLanguage}
                onLanguageChange={setTargetLanguage}
                excludeLanguage={i18n.language}
              />
            </div>
            
            <div className="flex flex-col gap-2">
              <h3 className="font-medium text-sm">{t("translations.textToTranslate")}</h3>
              <div className="max-h-60 overflow-y-auto p-3 border rounded-md bg-gray-50 text-sm">
                {text || t("translations.noTextProvided")}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleTranslate} disabled={isLoading}>
              {isLoading ? t("translations.translating") : t("translations.translate")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
